import React from 'react';
import { motion } from 'framer-motion';
import { 
  Github, 
  Mail, 
  Linkedin, 
  ArrowUp,
  Code
} from 'lucide-react';

export const Footer: React.FC = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const currentYear = new Date().getFullYear();

  const socialLinks = [
    {
      name: 'GitHub',
      url: 'https://github.com/eskuns',
      icon: <Github size={20} />,
      color: 'hover:bg-slate-700'
    },
    {
      name: 'LinkedIn',
      url: 'https://linkedin.com/in/enzo-marques-web',
      icon: <Linkedin size={20} />,
      color: 'hover:bg-primary-600'
    },
    {
      name: 'Email',
      url: 'mailto:enzo.marques@efrei.net',
      icon: <Mail size={20} />,
      color: 'hover:bg-red-500'
    }
  ];

  const navigationLinks = [
    { name: 'Accueil', href: '#home' },
    { name: 'À propos', href: '#about' },
    { name: 'Projets', href: '#projects' },
    { name: 'Compétences', href: '#skills' },
    { name: 'Expérience', href: '#experience' },
    { name: 'Contact', href: '#contact' }
  ];

  return (
    <footer className="bg-slate-900 text-white relative overflow-hidden">
      {/* Enhanced background decorations */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-950/20 to-primary-950/20" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-indigo-500 to-transparent" />
        
        {/* Floating dots */}
        <div className="absolute top-10 left-10 w-1 h-1 bg-indigo-400 rounded-full opacity-20 animate-float-dots" />
        <div className="absolute top-1/3 right-20 w-1.5 h-1.5 bg-primary-400 rounded-full opacity-15 animate-float-dots" 
             style={{ animationDelay: '-3s' }} />
        <div className="absolute bottom-1/4 left-1/4 w-1 h-1 bg-indigo-500 rounded-full opacity-10 animate-float-dots" 
             style={{ animationDelay: '-6s' }} />
        
        {/* Floating lines */}
        <div className="absolute top-1/4 right-0 h-px w-24 bg-gradient-to-l from-transparent via-indigo-400 to-transparent opacity-10 animate-float-lines" />
        <div className="absolute bottom-1/3 left-0 h-px w-32 bg-gradient-to-r from-transparent via-primary-400 to-transparent opacity-8 animate-float-lines" 
             style={{ animationDelay: '-4s' }} />
      </div>
      
      <div className="relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid lg:grid-cols-2 md:grid-cols-2 gap-8">
            {/* Brand section */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <motion.div
                  className="font-bold text-2xl bg-gradient-to-r from-indigo-400 to-primary-400 bg-clip-text text-transparent mb-4"
                  whileHover={{ scale: 1.05 }}
                >
                  Enzo Marques
                </motion.div>
                <p className="text-slate-400 mb-6 leading-relaxed max-w-md">
                  Développeur Web passionné, étudiant en Mastère Dev Manager Fullstack à EFREI Paris. 
                  À la recherche d'une alternance dès septembre 2025 pour développer mes compétences techniques.
                </p>
                <div className="flex space-x-3">
                  {socialLinks.map((link) => (
                    <motion.a
                      key={link.name}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`p-3 bg-slate-800 rounded-lg transition-all duration-300 ${link.color} text-slate-300 hover:text-white border border-slate-700 hover:border-slate-600 group`}
                      whileHover={{ scale: 1.1, y: -2 }}
                      whileTap={{ scale: 0.9 }}
                      aria-label={link.name}
                    >
                      <motion.div
                        whileHover={{ rotate: 5 }}
                        transition={{ duration: 0.2 }}
                      >
                        {link.icon}
                      </motion.div>
                    </motion.a>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Navigation à droite */}
            <div className="flex justify-end items-start">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
                className="w-full max-w-xs"
              >
                <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <motion.div
                    whileHover={{ rotate: 180 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Code size={20} className="text-indigo-400" />
                  </motion.div>
                  Navigation
                </h4>
                <ul className="space-y-3">
                  {navigationLinks.map((link) => (
                    <li key={link.name}>
                      <motion.a 
                        href={link.href} 
                        className="text-slate-400 hover:text-indigo-400 transition-colors duration-300 block py-1 group"
                        onClick={(e) => {
                          e.preventDefault();
                          document.querySelector(link.href)?.scrollIntoView({ behavior: 'smooth' });
                        }}
                        whileHover={{ x: 5 }}
                      >
                        <span className="border-b border-transparent group-hover:border-indigo-400 transition-colors duration-300">
                          {link.name}
                        </span>
                      </motion.a>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </div>

          {/* Enhanced Bottom section */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="border-t border-slate-800 mt-12 pt-8"
          >
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="flex items-center gap-2 text-slate-400">
                <span>&copy; {currentYear} Enzo Marques. Tous droits réservés.</span>
              </div>
              </div>
          </motion.div>
        </div>

        {/* Enhanced Scroll to top button */}
        <motion.button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 p-4 bg-gradient-to-r from-indigo-600 to-primary-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 z-50 border border-indigo-500/30 group"
          whileHover={{ scale: 1.1, y: -2 }}
          whileTap={{ scale: 0.9 }}
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          aria-label="Retour en haut"
        >
          <motion.div
            whileHover={{ y: -2 }}
            transition={{ duration: 0.2 }}
          >
            <ArrowUp size={20} />
          </motion.div>
          {/* Button pulse effect */}
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-indigo-600 to-primary-600 animate-ping opacity-20" />
          </motion.button>
      </div>
    </footer>
  );
};